/**
 * HelpTooltip - Iconița de ajutor (?) cu informații despre funcționalități.
 *
 * Componenta este unificată: la HOVER se afișează un tooltip scurt cu
 * titlul + descrierea, iar la CLICK se deschide un dialog detaliat care
 * conține și secțiunile suplimentare (pipeline, sfaturi etc.).
 */
import { useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Conținut de ajutor pentru fiecare secțiune.
// Cheia este pasată ca prop `section`. Dacă o cheie nu există aici, componenta
// va încerca să folosească props-urile `title`/`description` din apel.
// ---------------------------------------------------------------------------
export const helpTexts = {
  // ============= Tab-uri principale =============
  acasa: {
    title: "Acasă — Prezentare Generală",
    description: "Panoul principal afișează o privire de ansamblu asupra activității firmei: prezență angajați astăzi, notificări recente, statistici generale, total proiecte, unelte și utilizare.",
    sections: [
      { heading: "📊 Ce vezi aici", body: "• Prezențe astăzi — angajații care au făcut check-in.\n• Total proiecte active.\n• Total unelte și ore de utilizare.\n• Notificări necitite cu pastilă roșie." },
      { heading: "🔁 Acțiuni rapide", body: "• Generează pontaj automat pentru angajații care nu au făcut check-in.\n• Click pe orice card pentru detalii." },
    ],
  },

  notificari: {
    title: "Notificări",
    description: "Toate alertele și mesajele sistemului: check-in/out angajați, aprobări concedii, foi de parcurs completate, sarcini noi. Notificările necitite apar cu indicator roșu.",
    sections: [
      { heading: "📩 Tipuri de notificări", body: "• Check-in / check-out angajat.\n• Cerere de concediu de aprobat.\n• Foaie de parcurs nouă.\n• Sarcină nouă atribuită.\n• Alerta credite (sold scăzut, proformă scadentă)." },
      { heading: "✅ Cum le gestionezi", body: "Click pe o notificare pentru a o marca citită și a deschide pagina relevantă. Folosește „Marchează toate ca citite” pentru bulk." },
    ],
  },

  santiere: {
    title: "Șantiere / Proiecte",
    description: "Gestionează locațiile de lucru. Pentru fiecare șantier poți seta: adresa cu coordonate GPS, program de lucru, pauza de masă, echipa asignată și uneltele disponibile. Angajații pot face check-in doar pe șantierele la care sunt asignați.",
    sections: [
      { heading: "🏗️ Configurare șantier", body: "• Adresa + coordonate GPS pentru validare check-in.\n• Program de lucru (oră start / stop).\n• Pauza de masă (nu se pontează).\n• Echipa asignată.\n• Unelte disponibile pe acest șantier." },
      { heading: "💰 Financiar proiect", body: "• Valoare totală contract.\n• Cheltuieli înregistrate.\n• Progres procentual (0–100%).\n• Data început / sfârșit estimat." },
    ],
  },

  gantt: {
    title: "Diagrama Gantt — Cronologie Proiecte",
    description: "Vizualizare cronologică a tuturor proiectelor / șantierelor active. Fiecare bară reprezintă un proiect cu durata sa (start → end), iar lățimea este proporțională cu durata.",
    sections: [
      { heading: "📊 Cum citești diagrama", body: "• Bară verde — proiect activ (între data start și end).\n• Bară gri — proiect finalizat.\n• Procentajul afișat = progresul curent." },
      { heading: "🖱️ Acțiuni", body: "Click pe o bară pentru a deschide detaliile proiectului. Folosește filtrul pentru a vedea doar anumite perioade." },
    ],
  },

  unelte: {
    title: "Unelte & Vehicule",
    description: "Inventarul de unelte și vehicule al companiei. Uneltele pot fi asignate șantierelor, iar vehiculele sunt folosite pentru foile de parcurs — sistemul urmărește automat kilometrajul.",
    sections: [
      { heading: "🔧 Înregistrare unealtă", body: "• Nume + descriere.\n• Data achiziție.\n• Atașează factura PDF/imagine (utilă pentru garanție).\n• Numărul facturii pentru regăsire rapidă." },
      { heading: "📈 Statistici", body: "Sistemul calculează automat orele totale de utilizare a fiecărei unelte, agregate din pontajele angajaților." },
    ],
  },

  inventar: {
    title: "Inventar Produse & Materiale",
    description: "Gestiunea produselor și materialelor. Înregistrezi stocurile pe categorii, asignezi materiale unor devize și urmărești descărcările din gestiune.",
    sections: [
      { heading: "📦 Categorii", body: "• Materiale de construcții.\n• Consumabile.\n• Echipamente." },
      { heading: "🔁 Flux", body: "1. Înregistrezi achiziție (intrare).\n2. Asignezi material într-un Deviz.\n3. La finalizarea devizului, materialele se descarcă automat din stoc." },
    ],
  },

  angajati: {
    title: "Angajați",
    description: "Lista completă a angajaților firmei. Poți adăuga angajați noi (cu sau fără email), îi asignezi la șantiere și vizualizezi istoricul pontajului fiecăruia.",
    sections: [
      { heading: "👤 Tipuri de cont", body: "• Cu email — accesează aplicația mobilă, face check-in.\n• Fără email — administratorul îi pontează manual din calendar." },
      { heading: "🔐 Roluri", body: "• Worker — vede doar propriile pontaje, foi parcurs și sarcini.\n• Admin (cu drepturi) — poate aproba concedii, poate genera pontaje." },
    ],
  },

  pontaj: {
    title: "Pontaj — Calendar Prezență",
    description: "Calendarul de pontaj pentru toți angajații. Poți vedea și modifica statusul fiecărei zile: Prezent (P), Concediu Odihnă (CO), Concediu Medical (CM), Absent (A), Ore Suplimentare (OS).",
    sections: [
      { heading: "🗓️ Statusuri", body: "• P — Prezent (cu ore lucrate)\n• CO — Concediu Odihnă\n• CM — Concediu Medical\n• A — Absent (nepontaj)\n• OS — Ore Suplimentare (peste program normal)" },
      { heading: "✏️ Editare rapidă", body: "Click pe orice zi pentru a edita. Pentru weekend, poți adăuga doar ore suplimentare (OS)." },
    ],
  },

  foaieParcurs: {
    title: "Foaie de Parcurs",
    description: "Evidența deplasărilor cu vehiculele companiei. Angajații pornesc o foaie când pleacă (km inițial, destinație) și o completează la sosire (km final). Sistemul calculează automat distanța.",
    sections: [
      { heading: "🚗 Flux", body: "1. Angajatul deschide foaie de parcurs.\n2. Introduce km inițial + destinație.\n3. La sosire introduce km final.\n4. Sistemul calculează distanța și înregistrează în Registru." },
      { heading: "📊 Raportare", body: "Toate foile completate apar și în „Registru Documente” (folder Foi de Parcurs) cu număr unic de înregistrare." },
    ],
  },

  sarcini: {
    title: "Sarcini (Tichete)",
    description: "Sistem de tichete pentru organizarea muncii. Creezi sarcini, le asignezi angajaților, setezi prioritatea (scăzută/normală/urgentă) și urmărești progresul.",
    sections: [
      { heading: "📋 Atribute sarcină", body: "• Titlu + descriere.\n• Angajat responsabil.\n• Prioritate (scăzută / normală / urgentă).\n• Termen de finalizare.\n• Status (deschisă / în lucru / finalizată)." },
      { heading: "📱 Mobilă", body: "Angajații văd sarcinile lor în aplicația mobilă și pot marca finalizarea direct de pe teren." },
    ],
  },

  documente: {
    title: "Registru Documente",
    description: "Arhiva oficială cu numerotare automată. Toate cererile de concediu aprobate și foile de parcurs completate sunt înregistrate automat aici. Poți descărca documentele în format HTML sau PDF.",
    sections: [
      { heading: "📁 Foldere", body: "• Contracte Prestări Servicii\n• Foi de Parcurs\n• Cereri Concediu\n• Contracte Muncă\n• Fișe SSM/PSI\n• Alte documente" },
      { heading: "🔢 Numerotare", body: "Fiecare document primește un număr unic incremental pe firmă. Numerele sunt afișate ca badge violet (#Nr) în colțul stânga al cardului." },
      { heading: "📑 Contracte", body: "La contracte: ștergere = ANULAT (păstrează numărul). „Șterge definitiv” doar ascunde din UI, păstrează în DB." },
    ],
  },

  deviz: {
    title: "Generator Deviz F3 — Ghid Complet",
    description: "Generează devize (liste de cantități) pentru lucrări de construcții. Completează câmpurile galbene: beneficiar, lucrări, cantități și prețuri. Sistemul calculează automat: cheltuieli directe, indirecte, profit și TVA.",
    sections: [
      { heading: "🔄 Pipeline Deviz", body: "1. Creezi Deviz nou (status: Ciornă) → 2. Adaugi activități și prețuri → 3. Setezi procente (CCI 4%, Profit 5%, TVA 21%) → 4. Salvezi ca FINAL → 5. Poți genera CONTRACT din deviz → 6. Din contract creezi SITUAȚII DE LUCRĂRI (anexe) → 7. Din situații generezi FACTURI." },
      { heading: "🎯 Iconițele de acțiune", body: "• 📋 Contract (verde) — Generează contract din deviz (doar status FINAL). • 📑 Copie — Duplică devizul (consumă credite). • 👁 Vezi — Previzualizare PDF. • ✏️ Editare — Modifică. • 🗑 Șterge — Elimină devizul." },
      { heading: "🔼🔽 Reordonare lucrări", body: "În editorul de deviz, fiecare lucrare are numărul ei (1, 2, 3...) cu săgeți ▲▼ alături.\n• Click pe ▲ sau ▼ → mută lucrarea o poziție în sus/jos.\n• Click direct pe NUMĂR → apare un câmp unde tastezi noua poziție (ex: 5) și apeși Enter; lucrarea sare acolo iar restul se decalează automat.\nFuncție utilă când ai uitat o activitate la început și o adaugi pe ultimul rând — o tragi în sus printr-un click. Ordinea se păstrează și în PDF/print." },
      { heading: "💰 Procente Calcul", body: "Contribuție muncă (2.25%) = taxe pe salarii. Cheltuieli indirecte (4-15%) = costuri administrative. Profit (5-15%) = marja firmei. TVA (21%) = se aplică pe totalul final." },
      { heading: "📥 Format PDF", body: "Devizul descărcat are antet cu logo, datele firmei și beneficiarului, tabel cu activități, totale și locul de semnătură. Previzualizarea și printarea folosesc același format PDF." },
      { heading: "💡 Sfaturi", body: "• Folosește DevizPRO (AI) pentru sugestii automate de activități și prețuri.\n• Salvează preluări frecvente ca șabloane (duplicare).\n• La final, verifică totalul cu TVA înainte de a-l trimite beneficiarului." },
    ],
  },

  devizpro: {
    title: "DevizPRO — Estimator AI",
    description: "Generator automat de devize bazat pe inteligență artificială (GPT-4o). Descrii lucrarea în limbaj natural, iar sistemul îți sugerează activități, cantități și prețuri pe baza unei baze de cunoștințe.",
    sections: [
      { heading: "🤖 Cum funcționează", body: "1. Descrii lucrarea: \"Vopsesc 120 mp cameră 2 straturi, gletuiesc înainte\".\n2. AI generează lista de activități cu UM, cantități, preț materiale + manoperă.\n3. Importi lista direct într-un Deviz nou pe care îl poți edita." },
      { heading: "📚 Baza de cunoștințe", body: "Sistemul învață din toate devizele FINAL ale tuturor firmelor (anonim). Cu cât mai multe devize, cu atât estimările sunt mai precise." },
      { heading: "💳 Cost", body: "DevizPRO consumă credite (vezi preț în meniul Plăți)." },
    ],
  },

  contracte: {
    title: "Contracte Prestări Servicii",
    description: "Editor WYSIWYG pentru contracte. Generezi contract dintr-un deviz FINAL sau pornești de la șablon. Suport pentru anexe (situații de lucrări) cu numerotare secvențială.",
    sections: [
      { heading: "📝 Creare", body: "• Pornește din Deviz → buton „Generează Contract”.\n• Sau Generator Contracte → completezi datele manual.\n• Editor WYSIWYG pentru editare conținut.\n• Salvare cu număr unic per firmă." },
      { heading: "📎 Anexe / Situații de lucrări", body: "Fiecare contract poate avea anexe numerotate secvențial (Anexa 1, Anexa 2…). Din anexă generezi factură." },
      { heading: "❌ Ștergere", body: "• Anulat (Trash icon) — Marchează ANULAT, păstrează numărul.\n• Șterge definitiv — Ascunde din UI dar păstrează în DB (numărul rămâne ocupat)." },
    ],
  },

  facturi: {
    title: "Facturi",
    description: "Generare și gestionare facturi fiscale conform legislației române. Suport ANAF e-Factura (UBL XML), trimitere automată și descărcare semnătură fiscală.",
    sections: [
      { heading: "📤 Emisie", body: "• Manual — introduci articole.\n• Din deviz / contract — pre-completate automat.\n• Din situație de lucrări — preia totalul de pe anexă." },
      { heading: "🇷🇴 ANAF e-Factura", body: "Trimitere XML UBL către SPV. Statusul (received / accepted / rejected) se afișează în tabel. Descărcare ZIP cu semnătură fiscală după acceptare." },
      { heading: "💰 TVA", body: "TVA implicit 21%. Poți modifica per articol. Factura poate fi în RON sau EUR (cu cursul BNR)." },
    ],
  },

  plati: {
    title: "Plăți — Credite & Financiar",
    description: "Sistemul de credite e-Tesa. Solicit credite, primește instant alocarea și o proformă cu scadență 24h. După plată, proforma se transformă în factură fiscală.",
    sections: [
      { heading: "💳 Solicitare credite", body: "• Specifici numărul de credite (1 credit = 1 RON cu TVA).\n• Creditele se alocă INSTANT în contul tău.\n• Primești proforma pe email + în secțiunea Financiar." },
      { heading: "⏰ Scadență 24h", body: "Dacă nu achiți proforma în 24 ore, sistemul de credite se va SUSPENDA automat. Acțiunile plătite (devize noi, facturi, etc.) vor fi blocate." },
      { heading: "✅ După plată", body: "Click pe „Plată Online” pe proforma → se generează factura fiscală cu status ACHITATĂ. Soldul creditelor rămâne neschimbat (deja alocat)." },
    ],
  },

  // ============= Funcții specifice =============
  checkIn: {
    title: "Check-in Angajat",
    description: "Angajații fac check-in la începutul programului din aplicația mobilă. Sistemul înregistrează ora și locația GPS. Se poate seta validare de distanță pentru a verifica dacă angajatul este la șantier.",
  },
  concediu: {
    title: "Cerere de Concediu",
    description: "Angajații trimit cereri de concediu (odihnă, medical, fără plată). Administratorul aprobă sau respinge cererile. Cererile aprobate sunt înregistrate automat în calendar și în Registrul de Documente.",
  },
  oreSuplimentare: {
    title: "Ore Suplimentare",
    description: "Orele lucrate în afara programului normal sau în weekend. Se pot adăuga din calendarul de pontaj selectând statusul 'OS'. Sistemul ține evidența separată pentru raportare.",
  },
};

// ---------------------------------------------------------------------------
// Componenta unificată: hover = tooltip scurt, click = dialog detaliat.
// ---------------------------------------------------------------------------
export function HelpTooltip({ section, title, description, className = "" }) {
  const [open, setOpen] = useState(false);
  // Permite fallback la props când nu există secțiunea în helpTexts
  const help = (section && helpTexts[section]) || (title ? { title, description } : null);

  if (!help) return null;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 hover:bg-violet-100 text-slate-400 hover:text-violet-600 transition-colors ${className}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              data-testid={`help-tooltip-${section || 'inline'}`}
              aria-label={`Ajutor — ${help.title}`}
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-xs bg-slate-900 text-white p-3 rounded-lg shadow-xl"
          >
            <p className="font-semibold text-sm mb-1">{help.title}</p>
            <p className="text-xs text-slate-300 leading-relaxed">{help.description}</p>
            <p className="text-[10px] text-violet-300 mt-1.5">Click pentru detalii complete</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <HelpCircle className="w-5 h-5" />
              {help.title}
            </DialogTitle>
            <DialogDescription className="text-base text-slate-600 leading-relaxed pt-2">
              {help.description}
            </DialogDescription>
          </DialogHeader>
          {help.sections && help.sections.length > 0 && (
            <div className="space-y-3 pt-3 border-t">
              {help.sections.map((s, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-3">
                  <div className="font-semibold text-slate-800 text-sm mb-1">{s.heading}</div>
                  <div className="text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line">{s.body}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Componenta veche HelpDialog păstrată ca alias pentru compatibilitate cu
// codul existent care o importă. Acum este identică cu HelpTooltip pentru a
// evita dublarea iconițelor.
export function HelpDialog(props) {
  return <HelpTooltip {...props} />;
}

export default HelpTooltip;
